import React, { useId } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { TypedMemo } from '../../../common/types';
import { LockChip } from '../../../music24/components/LockedControl/LockedControl';
import { SMLUpgradeFeature } from '../../../music24/config/music24PlanCapabilities';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import cards from '../settingsCards.module.scss';
import styles from './notifications.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface Props {
  icon: IconDefinition;
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** The plan lacks this email: the switch shows off with the lock chip, and a click opens the upgrade prompt. */
  lockedFeature?: SMLUpgradeFeature;
  onLockedClick?: () => void;
  /** No line under the row: options for this switch follow. */
  flat?: boolean;
  divided?: boolean;
}

/** One email with its on/off switch (design settings-redesign c1, c2). */
const SwitchRowComponent: React.FC<Props> = ({
  icon,
  title,
  description,
  checked,
  onChange,
  lockedFeature,
  onLockedClick,
  flat = false,
  divided = false,
}) => {
  const id = useId();
  const isLocked = lockedFeature !== undefined;
  const rowClass = [cards.row, flat ? cards.rowFlat : '', divided ? styles.divided : '', isLocked ? styles.dimmed : '']
    .filter((name) => name !== '')
    .join(' ');

  return (
    <div className={rowClass}>
      <span className={`${cards.rowIcon}${isLocked ? '' : ` ${cards.rowIconBrand}`}`}>
        <FontAwesomeIcon icon={icon} />
      </span>
      <label className={cards.rowText} htmlFor={id}>
        <span className={cards.rowTitle}>{withBrandWord(title)}</span>
        <span className={cards.rowSub}>{withBrandWord(description)}</span>
      </label>
      <div className={cards.rowEnd}>
        {isLocked && <LockChip feature={lockedFeature} />}
        <div className={`form-check form-switch ${styles.switch}`}>
          <input
            id={id}
            type="checkbox"
            role="switch"
            className="form-check-input"
            checked={isLocked ? false : checked}
            onChange={(event) => (isLocked ? onLockedClick?.() : onChange(event.target.checked))}
          />
        </div>
      </div>
    </div>
  );
};

export const SwitchRow = TypedMemo(SwitchRowComponent);
